import React = require('react');
import { IPromise } from 'angular';
import { AngularContextFactory } from '../../../../ts/factories/AngularContextFactory';
import { CheckBoxWithLabelProps, DialogType } from '@tableau/widgets';
import { DialogAlertProps } from '../../../../common/react/components/dialogAlerts/DialogAlerts';
import { GrantLicenseModes } from '../../../../ts/ServerApi/requests/SyncActiveDirectoryGroupRequest';
import { Groups } from '../../Groups';
import { IActionContext } from '../../../../ts/helpers/ActionContext';
import { IGroupBasicInfo, IGroupInfo } from '../../../../ts/models/IGroupInfo';
import { IGroupDetailsInfo } from '../../../../ts/models/IGroupDetailsInfo';
import { IResult } from '../../../../ts/ServerApi/types/IResponse';
import { ISetGrantLicenseModeLocalGroupsDialogScope } from '../../../../ts/services/UpdateLocalGroupGrantLicenseModeAction';
import { ISiteRolePickerMenuScope } from '../../../../ts/helpers/SiteRolePickerMenuHelper';
import { L10n } from '../../../../ts/L10n/L10n';
import { Messages } from '../../../../codegen/Localize';
import { ModalInstance, ReactModalService } from '../../../../ts/services/ReactModalService';
import { ResultHelper } from '../../../../ts/helpers/ResultHelper';
import { ServiceLocator } from '../../../../app/ServiceLocator';
import { Set } from '../../../../ts/models/Set';
import { SiteRoleOptionsResource } from '../../../services/resources/SiteRoleOptions';
import { ToasterService, ToastType } from '../../../../ts/services/ToasterService';
import { UpdateGrantLicenseModeDialog } from '../../../../app/groups/UpdateGrantLicenseModeDialog';

export interface ISetGroupsMinimumSiteRoleDialogScope extends ISiteRolePickerMenuScope {
    description: string;
    siteRole: string;
    siteRoleOptions: string[];
    setSiteRole: (role: string) => void;
    licenseUponLoginCheckboxProps: CheckBoxWithLabelProps;
    grantLicenseMode: GrantLicenseModes;
    dialogInfoAlertProps: DialogAlertProps;
    isUnlicensed: boolean;
}

const groupUpdateDialogStyle: React.CSSProperties = {
    width: 'auto',
    height: 'auto',
    paddingTop: '18px',
    paddingBottom: '12px',
    paddingLeft: '12px',
    paddingRight: '12px'
};

function executeInPlace(group: IGroupDetailsInfo, siteRole: string, grantLicenseMode: GrantLicenseModes) {
    const groupSet = Set.fromIdItemArray([group]);
    const $q = ServiceLocator.get<ng.IQService>('$q');

    return Groups.updateGroupsMinimumSiteRole([group.id], siteRole, grantLicenseMode)
      .then(ResultHelper.rejectResultOnError($q))
      .then(() => handleSuccessResult(groupSet, siteRole))
      .catch((result: IResult) => handleErrorResult(groupSet, siteRole, result));
}

function showUpdateGroupsMinimumSiteRoleDialog(groupsToSet: Set<IGroupInfo>, context: IActionContext): IPromise<any> {
    let modalInstance: ModalInstance;
    const dialogScope = <ISetGrantLicenseModeLocalGroupsDialogScope> context.scope.$new();
    const angularContextFactory = ServiceLocator.get<AngularContextFactory>('AngularContextFactory');

    const dialogProps = {
      angularContext: angularContextFactory.create(dialogScope),
      dismiss: () => modalInstance && modalInstance.dismiss('cancel'),
      close: (result: any) => modalInstance && modalInstance.close(result),
      groupsToSet: groupsToSet,
      isActiveDirectory: true
    };

    modalInstance = ReactModalService.open({
      type: DialogType.Uber,
      dialogContent: React.createElement(UpdateGrantLicenseModeDialog, dialogProps),
      testId: 'updateLocalGroupGrantLicenseMode',
      showCloseButton: true,
      dialogStyle: groupUpdateDialogStyle,
      shouldNotSetDefaultWidth: false
    });

    modalInstance.result.finally(() => dialogScope.$destroy());

    return modalInstance.result;
}

function handleSuccessResult(groups: Set<IGroupBasicInfo>, siteRole: string) {
    ToasterService.instance.pop({
        type: ToastType.Info,
        text: Messages.updateGroupsSiteRoleSettingsSuccess({
            GROUP_NAME: L10n.elideIfLong(groups.first().name),
            COUNT: groups.size(),
            COUNT_LOC: L10n.Interpolations.Count
        })
    });
}

function handleErrorResult(groups: Set<IGroupBasicInfo>, siteRole: string, result: IResult) {
    ToasterService.instance.pop({
        type: ToastType.Error,
        text: Messages.updateGroupsMinimumSiteRoleError({
            GROUP_NAME: L10n.elideIfLong(groups.first().name),
            COUNT: groups.size(),
            SITE_ROLE: SiteRoleOptionsResource.instance.getSiteRoleName(siteRole)(),
            COUNT_LOC: L10n.Interpolations.Count
        })
    });
    return Promise.reject(result);
}

export const UpdateGroupsMinimumSiteRoleAction = {
    executeInPlace,
    showUpdateGroupsMinimumSiteRoleDialog
};

export type UpdateGroupsMinimumSiteRoleAction = typeof UpdateGroupsMinimumSiteRoleAction;
